import React, { Component } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { RichText } from "prismic-dom";
import Link from "gatsby-link";
import { Box, Flex } from "rebass";
import theme from "../theme.js";
import {
  Head,
  H1,
  H2,
  H3,
  H4,
  Text,
  Main,
  PageHeader,
  PageSection,
  Article,
  BlockQuote,
  ImageSlice,
  PageContent,
  PageSidebar,
  StaffProfile,
  UpdateMeta,
  TinyTile,
  CardLayout,
  UpdateCard,
} from "components";

const determineColor = function (region) {
  let color = "violet";
  if (
    region === "update" ||
    region === "institution" ||
    region === null ||
    region === undefined
  ) {
    color = "ash";
  }
  if (
    region === "middle-east-and-north-africa" ||
    region === "oriente-medio-y-norte-de-africa" ||
    region === "الشرق-الاوسط-وشمات-افريقيا"
  ) {
    color = "orange";
  }
  if (region === "africa" || region === "africa1" || region === "افريقيا") {
    color = "green";
  }
  if (
    region === "latin-america" ||
    region === "america-latina-y-el-caribe" ||
    region === "امريكا-اللاتينية"
  ) {
    color = "blue";
  }
  if (region === "asia" || region === "asia1" || region === "اسيا") {
    color = "pink";
  }
  if (
    region === "europe--central-asia" ||
    region === "europa-y-asia-central" ||
    region === "اوروبا-واسيا-الوسطى"
  ) {
    color = "yellow";
  }
  return color;
};

let linkResolver = function (doc) {
  return "/" + doc.type;
};

class Update extends React.Component {
  static contextTypes = {
    setAltLanguages: PropTypes.func,
  };
  componentDidMount() {
    if (this.props.data.page.alternate_languages) {
      this.context.setAltLanguages(this.props.data.page.alternate_languages);
    } else {
      this.context.setAltLanguages(undefined);
    }
    document.body.style.background =
      theme.colors[determineColor(this.props.data.page.data.region.uid)];
  }
  render() {
    let json = "";
    if (this.props.data.page.raw) {
      json = JSON.parse(this.props.data.page.raw);
    }
    if (this.props.data) {
      return (
        <div
          style={
            this.props.transition ? this.props.transition.style : { opacity: 0 }
          }
        >
          <Head
            source={{
              meta_title: [
                {
                  text:
                    json.data.title !== undefined
                      ? json.data.title[0]
                        ? json.data.title[0].text + " | Bank Information Center"
                        : ""
                      : "",
                },
              ],
              meta_description: [
                {
                  text:
                    json.data.excerpt !== undefined
                      ? json.data.excerpt[0]
                        ? json.data.excerpt[0].text
                        : ""
                      : "",
                },
              ],
              social_sharing_image: {
                url:
                  json.data.header_image !== undefined
                    ? json.data.header_image.url
                    : "",
              },
            }}
          />
          <Main>
            <PageHeader
              title={json.data.title[0].text}
              color={json.data.region ? json.data.region.uid : json.uid}
              image={
                json.data.header_image.url
                  ? [
                      json.data.header_image.url,
                      json.data.header_image.half.url,
                    ]
                  : ""
              }
            >
              {json.data.description[0] && (
                <Text children={json.data.description[0].text} />
              )}
            </PageHeader>
            <PageSection>
              <PageSidebar>
                {this.props.data.author && (
                  <UpdateMeta
                    updateText={
                      json.lang === "en-us"
                        ? "Last update"
                        : json.lang === "es-mx"
                        ? "Última actualización"
                        : "اخر تحديث"
                    }
                    attributionText={
                      json.lang === "en-us"
                        ? "Written by"
                        : json.lang === "es-mx"
                        ? "Escrito por"
                        : "كتب بواسطة"
                    }
                    staffName={
                      this.props.data.author.data.title[0]
                        ? this.props.data.author.data.title[0].text
                        : ""
                    }
                    staffImage={
                      this.props.data.author.data.header_image.url
                        ? this.props.data.author.data.header_image.quarter.url
                        : ""
                    }
                    secondaryStaffName={
                      this.props.data.secondary_author &&
                      this.props.data.secondary_author.data.title[0]
                        ? this.props.data.secondary_author.data.title[0].text
                        : ""
                    }
                    secondaryStaffImage={
                      this.props.data.secondary_author &&
                      this.props.data.secondary_author.data.header_image.url
                        ? this.props.data.secondary_author.data.header_image
                            .quarter.url
                        : ""
                    }
                    publishDate={
                      json.data.publish_date
                        ? moment(json.data.publish_date).format("MMMM Do, YYYY")
                        : ""
                    }
                    updateDate={
                      json.last_publication_date
                        ? moment(json.last_publication_date).format(
                            "MMMM Do, YYYY"
                          )
                        : ""
                    }
                    to={
                      this.props.data.author.lang +
                      "/team/" +
                      this.props.data.author.uid
                    }
                    secondaryTo={
                      this.props.data.secondary_author
                        ? this.props.data.author.lang +
                          "/team/" +
                          this.props.data.secondary_author.uid
                        : ""
                    }
                  />
                )}
                <Box mt={1}>
                  {this.props.data.region && (
                    <TinyTile
                      title={this.props.data.region.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.page.data.region.uid
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/region/" +
                        this.props.data.region.uid
                      }
                      type="Region"
                    />
                  )}
                  {this.props.data.institution && (
                    <TinyTile
                      title={this.props.data.institution.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution.uid
                      }
                      type="Institution"
                    />
                  )}
                  {this.props.data.institution2 && (
                    <TinyTile
                      title={this.props.data.institution2.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution2.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution2.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Institution"
                          : json.lang === "es-mx"
                          ? "Institucione"
                          : "لمؤسسة"
                      }
                    />
                  )}
                  {this.props.data.institution3 && (
                    <TinyTile
                      title={this.props.data.institution3.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.institution3.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/institution/" +
                        this.props.data.institution3.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Institution"
                          : json.lang === "es-mx"
                          ? "Institucione"
                          : "لمؤسسة"
                      }
                    />
                  )}
                  {this.props.data.project && (
                    <TinyTile
                      title={this.props.data.project.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.page.data.region.uid
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/project/" +
                        this.props.data.project.uid
                      }
                      type="Project"
                    />
                  )}
                  {this.props.data.campaign && (
                    <TinyTile
                      title={this.props.data.campaign.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign.uid
                      }
                      type="Campaign"
                    />
                  )}
                  {this.props.data.campaign2 && (
                    <TinyTile
                      title={this.props.data.campaign2.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign2.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign2.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                  {this.props.data.campaign3 && (
                    <TinyTile
                      title={this.props.data.campaign3.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign3.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign3.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                  {this.props.data.campaign4 && (
                    <TinyTile
                      title={this.props.data.campaign4.data.title[0].text}
                      cardColor={determineColor(
                        this.props.data.campaign4.data.type
                      )}
                      to={
                        "/" +
                        json.lang +
                        "/campaign/" +
                        this.props.data.campaign4.uid
                      }
                      type={
                        json.lang === "en-us"
                          ? "Campaign"
                          : json.lang === "es-mx"
                          ? "Campaña"
                          : "حملة"
                      }
                    />
                  )}
                </Box>
              </PageSidebar>
              <PageContent>
                <Box is="article">
                  {json.data.body[0] && (
                    <Box>
                      {json.data.body.map((slice, index) => {
                        if (slice.slice_type === "text") {
                          return (
                            <Article
                              is="div"
                              dangerouslySetInnerHTML={{
                                __html: RichText.asHtml(
                                  slice.primary.text,
                                  linkResolver
                                ),
                              }}
                            />
                          );
                        }
                        if (slice.slice_type === "image") {
                          return (
                            <ImageSlice
                              imageSet={[
                                slice.primary.image.url,
                                slice.primary.image.half.url,
                                slice.primary.image.quarter.url,
                              ]}
                              caption={slice.primary.caption}
                              alt={slice.primary.image.alt}
                            />
                          );
                        }
                        if (slice.slice_type === "quote") {
                          return (
                            <BlockQuote
                              quote={
                                slice.primary.quote[0]
                                  ? slice.primary.quote[0].text
                                  : ""
                              }
                              attribution={
                                slice.primary.name_of_the_author[0]
                                  ? slice.primary.name_of_the_author[0].text
                                  : ""
                              }
                            />
                          );
                        }
                      })}
                    </Box>
                  )}
                </Box>
              </PageContent>
            </PageSection>
            <PageSection column="true" borderTop="true">
              <PageContent>
                <H2 children="More Updates" mb={5} fontSize={[4, 5]} />
                {this.props.data.updates && (
                  <CardLayout>
                    {this.props.data.updates.edges
                      .slice(0, 4)
                      .map((update, index) => (
                        <UpdateCard
                          key={"update" + index}
                          object={update.node}
                        />
                      ))}
                  </CardLayout>
                )}
              </PageContent>
            </PageSection>
          </Main>
        </div>
      );
    } else {
      return null;
    }
  }
}

export default Update;

export const query = graphql`
  query UpdatePrismicQuery(
    $id: String!
    $lang: String!
    $author: String!
    $secondary_author: String!
    $region: String!
    $project: String!
    $institution: String!
    $institution2: String!
    $institution3: String!
    $campaign: String!
    $campaign2: String!
    $campaign3: String!
    $campaign4: String!
  ) {
    page: prismicDocument(prismicId: { eq: $id }) {
      raw
      uid
      id
      last_publication_date
      lang
      data {
        publish_date
        region {
          uid
        }
        title {
          type
          text
        }
        description {
          type
          text
        }
        header_image {
          url
          half {
            url
          }
        }
      }
      alternate_languages {
        uid
        lang
        type
      }
    }
    author: prismicDocument(prismicId: { eq: $author }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        description {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    secondary_author: prismicDocument(prismicId: { eq: $secondary_author }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        description {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    region: prismicDocument(prismicId: { eq: $region }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign: prismicDocument(prismicId: { eq: $campaign }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign2: prismicDocument(prismicId: { eq: $campaign2 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign3: prismicDocument(prismicId: { eq: $campaign3 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    campaign4: prismicDocument(prismicId: { eq: $campaign4 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution: prismicDocument(prismicId: { eq: $institution }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution2: prismicDocument(prismicId: { eq: $institution2 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    institution3: prismicDocument(prismicId: { eq: $institution3 }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    project: prismicDocument(prismicId: { eq: $project }) {
      uid
      type
      lang
      data {
        title {
          text
        }
        header_image {
          url
          quarter {
            url
          }
          half {
            url
          }
          thumb {
            url
          }
        }
      }
    }
    updates: allPrismicDocument(
      filter: { type: { eq: "update" }, lang: { eq: $lang } }
    ) {
      edges {
        node {
          uid
          lang
          type
          data {
            publish_date
            region {
              uid
            }
            title {
              text
            }
            description {
              text
            }
            header_image {
              url
              quarter {
                url
              }
              half {
                url
              }
              thumb {
                url
              }
            }
          }
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/layouts/update.js